import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import { Col, Row } from "react-grid-system";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import RoundButton from "../common/buttons/RoundButton";
import StyledButton from "../common/buttons/StyledButton";
import Container from "../common/Container";
import CMInfoChangedHistory from "./ChangedHistory";
import styles from "./CMInfo.module.css";
import CMInfoSubtools from "./CMInfoSubtools";
import CMInfoVerifiedHistory from "./CMInfoVerifiedHistory";
import toolStyles from "../../sharedStyles/Tool.module.css";
import { useChannelState } from "../../lib/channelContext";

const strings = {
  close: "Close",
  cmInfo: "CM Info",
  cmInfoChangedHistory: "CM Info History",
  cmInfoVerifiedHistory: "CM Verify History",
  verifiedAddressDateLabel: "Verified Address Date:",
  update: "UPDATE",
};

type Props = {
  dwbuid: string;
  onRequestClose: () => void;
  toolId: string;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  className?: string;
  referenceId?: string;
  onOpenPermissionToDiscussTool: () => void;
};

export default function CMInfo({
  dwbuid,
  onDisplayGeneralErrors,
  onRequestClose,
  toolId,
  onSetToolInProgress,
  className,
  referenceId,
  onOpenPermissionToDiscussTool,
}: Props) {
  const [currentTab, setCurrentTab] = useState<
    "cmInfo" | "cmInfoChangedHistory" | "cmInfoVerifiedHistory"
  >("cmInfo");

  const { channel } = useChannelState();

  const [verifiedAddressDate, setVerifiedAddressDate] = useState<string>();

  const resetButtonDiv = useRef<HTMLDivElement>(null);
  const updateButtonDiv = useRef<HTMLDivElement>(null);

  const onFormIsDirtyChanged = useCallback(
    (isDirty: boolean) => onSetToolInProgress(toolId, isDirty),
    [onSetToolInProgress, toolId],
  );

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col md={8} className={styles.titleRow}>
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.CMInfo)}
            </h2>
            <div className={styles.tabGroup}>
              <RoundButton
                className={styles.tabButton}
                variant={currentTab === "cmInfo" ? "blue" : "grey"}
                onClick={() => setCurrentTab("cmInfo")}
              >
                {strings.cmInfo}
              </RoundButton>
              <RoundButton
                className={styles.tabButton}
                variant={
                  currentTab === "cmInfoChangedHistory" ? "blue" : "grey"
                }
                onClick={() => setCurrentTab("cmInfoChangedHistory")}
              >
                {strings.cmInfoChangedHistory}
              </RoundButton>
              <RoundButton
                className={styles.tabButton}
                variant={
                  currentTab === "cmInfoVerifiedHistory" ? "blue" : "grey"
                }
                onClick={() => setCurrentTab("cmInfoVerifiedHistory")}
              >
                {strings.cmInfoVerifiedHistory}
              </RoundButton>
            </div>
            <div>
              <span className={styles.verifiedAddressDateLabel}>
                {strings.verifiedAddressDateLabel}
              </span>{" "}
              <span className={styles.verifiedAddressDateValue}>
                {verifiedAddressDate ?? strings.update}
              </span>
            </div>
          </Col>

          <Col className={toolStyles.centerCol} md={4}>
            <div className={styles.buttonGroup}>
              <div
                ref={resetButtonDiv}
                className={classNames({
                  [styles.buttonDiv]: true,
                  [styles.hidden]: currentTab !== "cmInfo",
                })}
              />
              <div
                ref={updateButtonDiv}
                className={classNames({
                  [styles.buttonDiv]: true,
                  [styles.hidden]: currentTab !== "cmInfo",
                })}
              />
              <div className={styles.buttonDiv}>
                <StyledButton
                  className={toolStyles.headerButton}
                  variant="secondary"
                  onClick={onRequestClose}
                >
                  {strings.close}
                </StyledButton>
              </div>
            </div>
          </Col>
        </Row>
        <div className={currentTab === "cmInfo" ? "" : styles.hidden}>
          <CMInfoSubtools
            dwbuid={dwbuid}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onFormIsDirtyChanged={onFormIsDirtyChanged}
            referenceId={referenceId}
            channel={channel}
            onVerifiedAddressDateChange={setVerifiedAddressDate}
            resetButtonDiv={resetButtonDiv}
            updateButtonDiv={updateButtonDiv}
            onOpenPermissionToDiscussTool={onOpenPermissionToDiscussTool}
          />
        </div>
        <div
          className={currentTab === "cmInfoChangedHistory" ? "" : styles.hidden}
        >
          <CMInfoChangedHistory dwbuid={dwbuid} referenceId={referenceId} />
        </div>
        <div
          className={
            currentTab === "cmInfoVerifiedHistory" ? "" : styles.hidden
          }
        >
          <CMInfoVerifiedHistory dwbuid={dwbuid} referenceId={referenceId} />
        </div>
      </Container>
    </div>
  );
}
